import _ from 'lodash'
import { useContext } from 'react'
import { generatePath, To } from 'react-router-dom'

import { useMergeFormData } from 'components/Form'
import { Table } from 'components/Table'
import { PaginationProps } from 'components/Table/Pagination'
import { useSort } from 'components/Table/Sort'
import { QueryProps } from 'features/inventory/domain/components/DomainsTable/types/api'
import {
  DomainsForTable,
  DomainSortBy,
} from 'features/inventory/domain/components/DomainsTable/types/model'
import { DomainListContext } from 'features/inventory/domain/contexts/DomainListContext'
import {
  DomainProductsAttributes,
  DomainTableSchema,
} from 'features/inventory/domain/forms/DomainTableForm/schema'
import { domainToProductsAttributes } from 'features/inventory/domain/pages/DomainList/utils'
import { useWorkspaceParam } from 'features/workspace/hooks/useWorkspaceParam'
import { useAbility } from 'providers/casl'
import { useTableSearchParams } from 'providers/router/hooks/useTableSearchParams'
import { useUrlSearchParams } from 'providers/router/hooks/useUrlSearchParams'
import { calculatePagination } from 'providers/tanstack-query/utils'
import { ROUTES } from 'routes'
import { DEFAULT_SORT, SORTABLE_COLUMNS } from './constants'
import { Alert, useGetAlerts } from './hooks/useGetAlerts'
import { useGetDomains } from './hooks/useGetDomains'
import { useColumns } from './useColumns'

export const DomainsTable = (): React.JSX.Element => {
  const ability = useAbility()
  const { workspaceId } = useWorkspaceParam()
  const { filters } = useContext(DomainListContext)
  const { page, rowsPerPage, setPage, setRowsPerPage } = useTableSearchParams()
  const { urlParams } = useUrlSearchParams()
  const { props: sortProps, sort } = useSort<DomainsForTable, DomainSortBy>(
    DEFAULT_SORT,
    SORTABLE_COLUMNS,
  )

  const queryVariables: QueryProps = {
    filters,
    ...calculatePagination({ page, rowsPerPage }),
    sort,
  }
  const { count, domains, loading } = useGetDomains(queryVariables)
  const alerts: Alert[] = useGetAlerts(queryVariables)
  const columns = useColumns({ alerts, count })

  const domainsAttributes: Record<string, DomainProductsAttributes> = _.chain(domains)
    .keyBy('id')
    .mapValues(domainToProductsAttributes)
    .value()

  useMergeFormData<DomainTableSchema>({
    data: domainsAttributes,
    path: 'domainsAttributes',
  })

  const handleRowClick = (row: DomainsForTable): To => ({
    pathname: generatePath(ROUTES.INVENTORY.DOMAIN.EDIT, {
      id: row.id,
      tab: 'general',
      workspaceId,
    }),
    search: urlParams,
  })

  const paginationProps: PaginationProps = {
    count,
    page,
    rowsPerPage,
    setPage,
    setRowsPerPage,
  }

  const canAccessDomainEdit: boolean = ability.can('access', 'DomainPage-edit')

  return (
    <Table<DomainsForTable>
      columns={columns}
      data={domains}
      loading={loading}
      meta={{ stickyHeader: true }}
      onClickRow={canAccessDomainEdit ? id => handleRowClick(id) : undefined}
      pagination={paginationProps}
      sort={sortProps}
    />
  )
}
